import { useTranslation } from 'react-i18next'

import { ResultSection } from 'aem-libs-react-components/src/components/calculators/components/result-section'
import { Flex } from 'aem-libs-react-components/src/components/common/flex'
import { useDeeplink } from 'aem-libs-react-ts/src/hooks/useDeeplink'
import { useDynamicParams } from 'aem-libs-react-ts/src/hooks/useDynamicParams'
import LanguageDetector from 'aem-libs-react-ts/src/services/LanguageDetector'

import { testIds } from '../e2e/testIds'
import { useCalculateChildrenSavingsQuery } from '../hooks/useCalculateChildrenSavingsQuery'
import { childrenSavingsStore } from '../store'
import type { ChildrenSavingsCalculatorParams } from '../types'

const lang = LanguageDetector()

export const ResultBoard = () => {
  const { t } = useTranslation()
  const values = childrenSavingsStore.useStoreData()
  const response = useCalculateChildrenSavingsQuery(values)
  const params = useDynamicParams<ChildrenSavingsCalculatorParams>()
  const georgeDeeplink = useDeeplink(
    {
      url: params.orderGeorgeMobileButton.url[lang],
      fallbackPath: params.orderGeorgeMobileButton.fallbackPath,
      hash: params.orderGeorgeMobileButton.hash,
      medium: params.orderGeorgeMobileButton.medium
    },
    null,
    Boolean(params.orderGeorgeMobileButton.isDeepLink)
  )

  return (
    <ResultSection.Container loading={response.isFetching}>
      <ResultSection.SimpleRow label={t('RESULT.TOTAL_SAVINGS')} withBorder>
        <ResultSection.AnimatedValue
          value={response?.data?.untilLegalAgeCalculation.totalSavingsAmount}
          unitType={'czk'}
          ariaLabel={t('RESULT.TOTAL_SAVINGS')}
          testId={testIds.savingsTotalResult}
        />
      </ResultSection.SimpleRow>
      <ResultSection.SimpleRow label={t('RESULT.TOTAL_RETURN')} withBorder>
        <ResultSection.Value
          value={response?.data?.untilLegalAgeCalculation.totalReturn}
          unitType={'czk'}
          testId={testIds.savingsReturnResult}
        />
      </ResultSection.SimpleRow>
      <ResultSection.Highlight>
        <Flex justifyContent={'space-between'}>
          <span>{t('RESULT.ONE_YEAR_SAVINGS')}</span>
          <ResultSection.Value
            value={response?.data?.oneYearCalculation.totalSavingsAmount}
            unitType={'czk'}
            testId={testIds.savingsOneYearSavingsResult}
          />
        </Flex>
        <Flex justifyContent={'space-between'}>
          <span>{t('RESULT.ONE_YEAR_RETURN')}</span>
          <ResultSection.Value
            value={response?.data?.oneYearCalculation.totalReturn}
            unitType={'czk'}
            testId={testIds.savingsOneYearReturnResult}
          />
        </Flex>
      </ResultSection.Highlight>
      <ResultSection.Cta
        testId={testIds.cta}
        items={[
          {
            label: params.georgeButtonConfig.label[lang],
            showButton: params.georgeButtonConfig.showButton,
            url: params.georgeButtonConfig.url[lang],
            showOnDevice: 'desktop',
            isDeeplink: false,
            order: params.georgeButtonConfig.order,
            type: 'george',
            theme: params.georgeButtonConfig.theme
          },
          {
            label: params.orderGeorgeMobileButton.label[lang],
            url: georgeDeeplink.link,
            showButton: params.orderGeorgeMobileButton.showButton,
            showOnDevice: 'mobile',
            type: 'george',
            isDeeplink: params.orderGeorgeMobileButton.isDeepLink,
            onClick: georgeDeeplink.redirect,
            order: params.orderGeorgeMobileButton.order,
            theme: params.orderGeorgeMobileButton.theme
          },
          {
            label: params.callMeButtonConfig.label[lang],
            showButton: params.callMeButtonConfig.showButton,
            url: params.callMeButtonConfig.url[lang],
            type: 'callMe',
            order: params.callMeButtonConfig.order,
            theme: params.callMeButtonConfig.theme
          },
          {
            label: params.branchArrangeButtonConfig.label[lang],
            showButton: params.branchArrangeButtonConfig.showButton,
            url: params.branchArrangeButtonConfig.url[lang],
            type: 'branchArrange',
            order: params.branchArrangeButtonConfig.order,
            theme: params.branchArrangeButtonConfig.theme
          }
        ]}
      />
    </ResultSection.Container>
  )
}
